// Shared image picker for the add/edit drink forms: several images per drink,
// each with a caption, each able to have its background stripped.
import { Image } from './types';
import { imageUrl, removeBackground } from './api';

interface Row {
  id?: string; // already on the server
  blob?: Blob; // newly picked, or a background-stripped replacement
  caption: string;
  url: string; // preview source
}

export interface ImagePicker {
  /** Adds the kept-image list and any new files to the outgoing form data. */
  appendTo(data: FormData): void;
}

export function renderImagePicker(container: HTMLElement, existing: Image[] = []): ImagePicker {
  const rows: Row[] = existing.map((img) => ({
    id: img.id,
    caption: img.caption,
    url: imageUrl(img.id),
  }));

  container.innerHTML = `
    <div class="image-rows"></div>
    <input type="file" class="image-add" accept="image/*" multiple>
    <p class="image-hint">First image is the cover — drag rows to reorder. Captions are optional.</p>
  `;
  const list = container.querySelector('.image-rows') as HTMLElement;

  const draw = () => {
    list.innerHTML = rows
      .map(
        (row, i) => `
        <div class="image-row" data-i="${i}" draggable="true">
          <img src="${row.url}" alt="">
          <input type="text" class="image-caption" placeholder="Caption" value="${row.caption.replace(/"/g, '&quot;')}">
          <button type="button" class="btn-bg">Remove bg</button>
          <button type="button" class="btn-drop">&times;</button>
        </div>`
      )
      .join('');
  };
  draw();

  // Drag a row onto another to reorder; the list order is the carousel order.
  let dragFrom: number | null = null;
  list.addEventListener('dragstart', (e) => {
    const row = (e.target as HTMLElement).closest('.image-row') as HTMLElement | null;
    if (!row) return;
    dragFrom = +row.dataset.i!;
    row.classList.add('image-row-dragging');
  });
  list.addEventListener('dragover', (e) => e.preventDefault()); // allow the drop
  list.addEventListener('drop', (e) => {
    e.preventDefault();
    const row = (e.target as HTMLElement).closest('.image-row') as HTMLElement | null;
    if (!row || dragFrom === null) return;
    const to = +row.dataset.i!;
    if (to === dragFrom) return;
    syncCaptions();
    rows.splice(to, 0, ...rows.splice(dragFrom, 1));
    dragFrom = null;
    draw();
  });
  list.addEventListener('dragend', () => {
    list.querySelector('.image-row-dragging')?.classList.remove('image-row-dragging');
    dragFrom = null;
  });

  // Captions are read straight off the inputs on submit, so typing needs no
  // redraw — anything that changes the list itself redraws.
  list.addEventListener('click', async (e) => {
    const target = e.target as HTMLElement;
    const rowEl = target.closest('.image-row') as HTMLElement | null;
    if (!rowEl) return;
    const i = +rowEl.dataset.i!;
    syncCaptions();

    if (target.classList.contains('btn-drop')) {
      rows.splice(i, 1);
      draw();
      return;
    }

    if (target.classList.contains('btn-bg')) {
      const btn = target as HTMLButtonElement;
      const label = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Working…';
      try {
        // An existing image lives on the server; fetch it back to send it on.
        const source = rows[i].blob ?? (await (await fetch(rows[i].url)).blob());
        const stripped = await removeBackground(source);
        // The result replaces the row: a stored original it came from is
        // dropped from the keep list, so the server deletes it on save.
        rows[i] = { blob: stripped, caption: rows[i].caption, url: URL.createObjectURL(stripped) };
        draw();
      } catch (err) {
        alert((err as Error).message);
        btn.disabled = false;
        btn.textContent = label;
      }
    }
  });

  (container.querySelector('.image-add') as HTMLInputElement).addEventListener('change', (e) => {
    const input = e.target as HTMLInputElement;
    syncCaptions();
    for (const file of Array.from(input.files || [])) {
      rows.push({ blob: file, caption: '', url: URL.createObjectURL(file) });
    }
    input.value = ''; // let the same file be picked again after a removal
    draw();
  });

  function syncCaptions(): void {
    list.querySelectorAll('.image-row').forEach((el) => {
      const i = +(el as HTMLElement).dataset.i!;
      rows[i].caption = (el.querySelector('.image-caption') as HTMLInputElement).value;
    });
  }

  return {
    appendTo(data: FormData) {
      syncCaptions();
      const keep = rows.filter((r) => r.id).map((r) => ({ id: r.id, caption: r.caption }));
      data.append('images', JSON.stringify(keep));
      // New files and their captions pair up by position on the server.
      rows
        .filter((r) => r.blob)
        .forEach((r, n) => {
          const name = r.blob instanceof File ? r.blob.name : `image-${n}.png`;
          data.append('image', r.blob!, name);
          data.append('caption', r.caption);
        });
    },
  };
}

// The cover image stays a plain picture in the detail view. The rest live in a
// thumbnail strip that opens a full-screen viewer.
export function renderThumbs(images: Image[]): string {
  if (images.length < 2) return '';
  return `
    <div class="thumb-strip">
      ${images
        .map(
          (img, i) =>
            `<button type="button" class="thumb" data-i="${i}" title="${escapeAttr(img.caption)}">
               <img src="${imageUrl(img.id)}" alt="${escapeAttr(img.caption)}">
             </button>`
        )
        .join('')}
    </div>
  `;
}

export function initThumbs(root: HTMLElement, images: Image[]): void {
  root.querySelectorAll('.thumb').forEach((btn) =>
    btn.addEventListener('click', () => openLightbox(images, +(btn as HTMLElement).dataset.i!))
  );
}

function escapeAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

// Full-screen viewer: arrows, keyboard, captions. Closes on Esc or backdrop.
export function openLightbox(images: Image[], start = 0): void {
  let i = start;

  const box = document.createElement('div');
  box.className = 'lightbox';
  box.innerHTML = `
    <button type="button" class="lightbox-close" aria-label="Close">&times;</button>
    <button type="button" class="lightbox-nav lightbox-prev" aria-label="Previous">&#8249;</button>
    <figure class="lightbox-figure">
      <img class="lightbox-image" src="" alt="">
      <figcaption class="lightbox-caption"></figcaption>
    </figure>
    <button type="button" class="lightbox-nav lightbox-next" aria-label="Next">&#8250;</button>
    <span class="lightbox-count"></span>
  `;
  document.body.appendChild(box);
  requestAnimationFrame(() => box.classList.add('active'));

  const img = box.querySelector('.lightbox-image') as HTMLImageElement;
  const caption = box.querySelector('.lightbox-caption') as HTMLElement;
  const count = box.querySelector('.lightbox-count') as HTMLElement;

  const show = (step: number) => {
    i = (i + step + images.length) % images.length;
    img.src = imageUrl(images[i].id);
    caption.textContent = images[i].caption;
    count.textContent = `${i + 1} / ${images.length}`;
  };
  show(0);

  const close = () => {
    document.removeEventListener('keydown', onKey);
    box.classList.remove('active');
    setTimeout(() => box.remove(), 200);
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') close();
    if (e.key === 'ArrowLeft') show(-1);
    if (e.key === 'ArrowRight') show(1);
  };

  document.addEventListener('keydown', onKey);
  box.querySelector('.lightbox-prev')!.addEventListener('click', () => show(-1));
  box.querySelector('.lightbox-next')!.addEventListener('click', () => show(1));
  box.querySelector('.lightbox-close')!.addEventListener('click', close);
  box.addEventListener('click', (e) => {
    if (e.target === box) close();
  });
}
